import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import { clipAudioApi } from '../../services/api';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  MusicNote,
  Movie,
  Image as ImageIcon,
  Animation,
  PlayArrow
} from '@mui/icons-material';
import Step1ThemePrompt from '../../components/ClipAudioWorkflow/StepsComponents/Step1ThemePrompt';
import Step2SceneGeneration from '../../components/ClipAudioWorkflow/StepsComponents/Step2SceneGeneration';
import Step3ImageGeneration from '../../components/ClipAudioWorkflow/StepsComponents/Step3ImageGeneration';
import Step4Animation from '../../components/ClipAudioWorkflow/StepsComponents/Step4Animation';

function ClipAudioPipelineScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Project state
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Current project state
  const [currentStep, setCurrentStep] = useState(0);
  
  // Project data state - unified structure that gets updated through workflow
  const [projectData, setProjectData] = useState({
    // Initial project data (from creation)
    name: null,
    theme: null,
    lyrics: null,
    // Workflow data (generated through steps)
    song: null,
    scenes: null,
    images: null
  });

  // Fetch project from API
  const fetchProject = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await clipAudioApi.getProject(id);
      setProject(data);
      setCurrentStep(data.current_step);
      
      // Set project data from API response - merge initial data with workflow data
      setProjectData(prev => ({
        ...prev,
        // Initial project data
        name: data.name,
        theme: data.theme,
        current_step: data.current_step,
        step_status: data.step_status,
        // Workflow data (if exists)
        ...(data.workflow_data || {}),
        // Extract lyrics from workflow_data.lyric.lyric if it exists
        lyrics: data.workflow_data?.lyric?.lyric || data.lyrics
      }));
    } catch (err) {
      console.error('Error fetching project:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (id) {
      fetchProject();
    }
  }, [id]);

  const handleSongGenerated = (song) => {
    setProjectData(prev => ({ ...prev, song }));
    // Don't automatically advance to step 2 - let user control the flow
  };

  const handleScenesGenerated = (scenes) => {
    setProjectData(prev => ({ ...prev, scenes }));
    setCurrentStep(3);
  };

  const handleImagesGenerated = (images) => {
    setProjectData(prev => ({ ...prev, images }));
    setCurrentStep(4);
  };

  const handleNextStep = (data) => {
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
  };

  const handleAnimationComplete = () => {
    // Project completed - redirect back to project list
    navigate('/audio');
  };

  const handleBackToList = () => {
    navigate('/audio');
  };

  const getStepLabel = (step) => {
    switch (step) {
      case 0: return "Projet terminé";
      case 1: return "Paroles";
      case 2: return "Scènes";
      case 3: return "Images";
      case 4: return "Animation";
      default: return "Projet terminé";
    }
  };

  const getStepIcon = (step) => {
    switch (step) {
      case 0: return <PlayArrow />;
      case 1: return <MusicNote />;
      case 2: return <Movie />;
      case 3: return <ImageIcon />;
      case 4: return <Animation />;
      default: return <PlayArrow />;
    }
  };

  const getStepColor = (step) => {
    switch (step) {
      case 0: return "default";
      case 1: return "success";
      case 2: return "warning";
      case 3: return "secondary";
      case 4: return "info";
      default: return "default";
    }
  };

  // Render step components
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1ThemePrompt
            projectId={id}
            projectData={projectData}
            onSongGenerated={handleSongGenerated}
            onNextStep={handleNextStep}
            onRefreshProject={fetchProject}
          />
        );
      case 2:
        return (
          <Step2SceneGeneration
            projectId={id}
            projectData={projectData}
            onScenesGenerated={handleScenesGenerated}
            onNextStep={handleNextStep}
            onRefreshProject={fetchProject}
          />
        );
      case 3:
        return (
          <Step3ImageGeneration
            projectId={id}
            projectData={projectData}
            onImagesGenerated={handleImagesGenerated}
            onNextStep={handleNextStep}
            onRefreshProject={fetchProject}
          />
        );
      case 4:
        return (
          <Step4Animation
            projectId={id}
            projectData={projectData}
            onAnimationComplete={handleAnimationComplete}
            onRefreshProject={fetchProject}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
        <Navigation />
        <Container maxWidth="xl" sx={{ py: 3 }}>
        <Card sx={{ borderRadius: 4, bgcolor: '#FFFFFF', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2, color: '#3498DB' }} />
            <Typography variant="h4" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
              Chargement du projet...
            </Typography>
            <Typography variant="h6" sx={{ color: '#7F8C8D' }}>
              Veuillez patienter pendant que nous récupérons les détails du projet.
            </Typography>
          </CardContent>
        </Card>
      </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
        <Navigation />
        <Container maxWidth="xl" sx={{ py: 3 }}>
        <Card sx={{ borderRadius: 4, bgcolor: '#FFFFFF', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                Erreur lors du chargement du projet
              </Typography>
              <Typography variant="body1">
                {error}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                Vérifiez que le serveur API est démarré
              </Typography>
            </Alert>
            <Button
              variant="contained"
              onClick={() => navigate('/audio')}
              sx={{
                bgcolor: '#3498DB',
                '&:hover': { bgcolor: '#2980B9' }
              }}
            >
              Retour aux projets
            </Button>
          </CardContent>
        </Card>
      </Container>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
        <Navigation />
        <Container maxWidth="xl" sx={{ py: 3 }}>
        <Card sx={{ borderRadius: 4, bgcolor: '#FFFFFF', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
              Projet non trouvé
            </Typography>
            <Typography variant="h6" sx={{ color: '#7F8C8D', mb: 3 }}>
              Le projet demandé n'existe pas ou a été supprimé.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/audio')}
              sx={{
                bgcolor: '#3498DB',
                '&:hover': { bgcolor: '#2980B9' }
              }}
            >
              Retour aux projets
            </Button>
          </CardContent>
        </Card>
      </Container>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
      <Navigation />
      <Container maxWidth="xl" sx={{ py: 3 }}>
      <Card sx={{ 
        borderRadius: 4, 
        bgcolor: '#FFFFFF',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <CardContent sx={{ p: 4 }}>
          {/* Compact Project Header with Back Button */}
          <Paper sx={{ 
            bgcolor: '#F8F9FA', 
            borderRadius: 2, 
            p: 2,
            mb: 3,
            border: '1px solid #E1E8ED'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 'bold', 
                  color: '#2C3E50'
                }}>
                  {project.name}
                </Typography>
                <Chip
                  icon={getStepIcon(currentStep)}
                  label={`Étape ${currentStep}: ${getStepLabel(currentStep)}`}
                  color={getStepColor(currentStep)}
                  variant="filled"
                  size="medium"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
              <Button
                onClick={handleBackToList}
                startIcon={<ArrowBack />}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: '#95A5A6',
                  color: '#7F8C8D',
                  fontWeight: 'bold',
                  '&:hover': {
                    borderColor: '#7F8C8D',
                    backgroundColor: '#F8F9FA'
                  }
                }}
              >
                Retour
              </Button>
            </Box>
          </Paper>

          {renderCurrentStep()}
        </CardContent>
      </Card>
    </Container>
    </div>
  );
}

export default ClipAudioPipelineScreen;
